import { useEffect, useRef } from 'react';
import {
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

import { asHTMLElement } from './DatePicker.helpers';

const CHEVRON_DOWN_ICON = '▾';

export interface DatePickerDropdownOption {
  label: string | number;
  value: number;
}

interface DatePickerDropdownProps {
  label: string | number;
  options: DatePickerDropdownOption[];
  selectedValue: number;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (selectedValue: number) => void;
  accessibilityLabel: string;
  style?: StyleProp<ViewStyle>;
  alignItemsCenter?: boolean;
}

const DatePickerDropdown = ({
  label,
  options,
  selectedValue,
  isOpen,
  onToggle,
  onSelect,
  accessibilityLabel,
  style,
  alignItemsCenter = false,
}: DatePickerDropdownProps) => {
  const triggerRef = useRef<View>(null);
  const selectedOptionRef = useRef<View>(null);

  useEffect(() => {
    if (isOpen && selectedOptionRef.current) {
      asHTMLElement(selectedOptionRef.current)?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [isOpen]);

  const handleSelectOption = (valueToSelect: number) => {
    onSelect(valueToSelect);
    asHTMLElement(triggerRef.current)?.focus();
  };

  return (
    <View
      style={[
        styles.dropdownAnchor,
        style,
        isOpen && styles.dropdownAnchorOpen,
      ]}
    >
      <Pressable
        ref={triggerRef}
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded: isOpen }}
        style={({ hovered }) => [
          styles.dropdownTrigger,
          isOpen && styles.dropdownTriggerActive,
          hovered &&
            !isOpen && { borderColor: theme.color.interactive.primary },
        ]}
      >
        <BodyBaseText style={styles.dropdownTriggerText}>{label}</BodyBaseText>
        <BodySmText style={styles.chevronIcon}>{CHEVRON_DOWN_ICON}</BodySmText>
      </Pressable>

      {isOpen ? (
        <View style={styles.dropdownMenu} accessibilityRole="list">
          <ScrollView
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            style={styles.dropdownScroll}
          >
            {options.map((dropdownOption) => {
              const isOptionSelected = dropdownOption.value === selectedValue;
              return (
                <Pressable
                  key={dropdownOption.value}
                  ref={isOptionSelected ? selectedOptionRef : undefined}
                  onPress={() => {
                    handleSelectOption(dropdownOption.value);
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isOptionSelected }}
                  style={({ hovered }) => [
                    styles.dropdownItem,
                    alignItemsCenter && styles.dropdownItemCentered,
                    isOptionSelected && styles.dropdownItemSelected,
                    hovered && !isOptionSelected && styles.dropdownItemHovered,
                  ]}
                >
                  <BodyBaseText
                    style={[
                      styles.dropdownItemText,
                      isOptionSelected && styles.dropdownItemTextSelected,
                    ]}
                  >
                    {dropdownOption.label}
                  </BodyBaseText>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownAnchor: {
    position: 'relative',
    zIndex: 10,
  },
  dropdownAnchorOpen: {
    zIndex: 20,
  },
  dropdownTrigger: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    borderWidth: 1,
    borderColor: theme.color.border.primarySolid,
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: theme.color.background.primary,
  },
  dropdownTriggerActive: {
    borderColor: theme.color.interactive.primary,
    backgroundColor: theme.color.border.focusedInverse,
  },
  dropdownTriggerText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.color.text.primary,
  },
  chevronIcon: { fontSize: 10, color: theme.color.interactive.primary },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    marginTop: 6,
    left: 0,
    width: '100%',
    maxHeight: 180,
    backgroundColor: theme.color.background.primary,
    borderWidth: 1,
    borderColor: theme.color.border.focusedInverse,
    borderRadius: 14,
    zIndex: 1001,
    padding: theme.spacing.xs,
    shadowColor: theme.color.text.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 6,
  },
  dropdownScroll: { maxHeight: 160 },
  dropdownItem: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  dropdownItemCentered: {
    alignItems: 'center',
  },
  dropdownItemSelected: { backgroundColor: theme.color.border.focusedInverse },
  dropdownItemHovered: {
    backgroundColor: theme.color.interactive.surfaceHovered,
  },
  dropdownItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.color.text.primary,
  },
  dropdownItemTextSelected: {
    color: theme.color.interactive.primary,
    fontWeight: '700',
  },
});

export default DatePickerDropdown;
